import type { ProductWithAccuratePricingData } from "@/hooks";
import { useSellersMap } from "@/queries/sellers";
import {
  combineOfferPriceStates,
  type OfferPriceResolver
} from "@/utils/offerPriceState";
import { formattedProductOverviewData } from "@/utils/product";
import React from "react";
import ProductPreviewCard, {
  ProductPreviewCardLoader
} from "./ProductPreviewCard";

type BaseProduct = Pick<
  ProductWithAccuratePricingData,
  | "id"
  | "status"
  | "title"
  | "thumbnail"
  | "brand"
  | "categories"
  | "meta"
  | "variants"
>;
const DEFAULT: any[] = [];

interface ProductsShowCaseProps<T extends BaseProduct> {
  status: "pending" | "error" | "success";
  pricingStatus: "pending" | "error" | "success";
  items?: T[];
  /**
   * Whether an offer may show its price yet (KAN-343). A card's price range
   * spans all of its product's offers, so it is held back while any of them
   * cannot. Unset means every price can show, as when the API already priced
   * the items for this salon.
   */
  offerPriceState?: OfferPriceResolver;
}

function ProductsShowCase<T extends BaseProduct>({
  status,
  items = DEFAULT,
  offerPriceState
}: ProductsShowCaseProps<T>) {
  const { data: sellersMap } = useSellersMap();
  if (status === "error") {
    // TODO better UI
    return <div>Sorry there was an error</div>;
  }

  const productCards =
    status === "success"
      ? items.map((product: T) => {
          const formattedProps = formattedProductOverviewData(
            product,
            sellersMap
          );

          const priceState = offerPriceState
            ? combineOfferPriceStates(
                product.variants.flatMap((variant) =>
                  variant.seller_offers.map((offer) => offerPriceState(offer))
                )
              )
            : "ready";

          return (
            <ProductPreviewCard
              //
              key={formattedProps.offerId}
              {...formattedProps}
              priceState={priceState}
            />
          );
        })
      : Array.from({ length: 4 }).map((_, i) => (
          <ProductPreviewCardLoader key={i} />
        ));

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      {productCards}
    </div>
  );
}

export default React.memo(ProductsShowCase);
